import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LanguageRouteWrapper } from "./components/LanguageRouteWrapper";
import { AnimatePresence, motion } from "framer-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "styled-components";
import { Toaster } from "sonner";
import { theme } from "./styles/theme";
import { GlobalStyle } from "./styles/GlobalStyle";
import { AppLayout } from "./components/layout/AppLayout";
import { ScrollToTop } from "./components/layout/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { loadAndSetLanguage } from "./store/reducers/translationReducer";
import languageConfig from "./config/languageConfig";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import GetStarted from "./pages/GetStarted";

import LandingHome from "./pages/landing/Home";
import Features from "./pages/landing/Features";
import Pricing from "./pages/landing/Pricing";
import About from "./pages/landing/About";
import Contact from "./pages/landing/Contact";
import Integrations from "./pages/landing/Integrations";
import Changelog from "./pages/landing/Changelog";
import Blog from "./pages/landing/Blog";
import BlogPost from "./pages/landing/BlogPost";
import Careers from "./pages/landing/Careers";
import Documentation from "./pages/landing/Documentation";
import HelpCenter from "./pages/landing/HelpCenter";
import APIReference from "./pages/landing/APIReference";
import Status from "./pages/landing/Status";
import Privacy from "./pages/landing/Privacy";
import Terms from "./pages/landing/Terms";
import Security from "./pages/landing/Security";
import Stores from "./pages/Stores";
import Products from "./pages/Products";
import ProductDetails from "./pages/ProductDetails";
import Orders from "./pages/Orders";
import OrderDetails from "./pages/OrderDetails";
import Customers from "./pages/Customers";
import CustomerOrderHistory from "./pages/CustomerOrderHistory";
import Users from "./pages/Users";
import Tasks from "./pages/Tasks";
import TaskDetails from "./pages/TaskDetails";
import Analytics from "./pages/Analytics";
import Billing from "./pages/Billing";
import Support from "./pages/Support";
import TicketDetail from "./pages/TicketDetail";
import ArticleDetail from "./pages/ArticleDetail";
import Conversations from "./pages/Conversations";
import Coupons from "./pages/Coupons";
import Extensions from "./pages/Extensions";
import Marketing from "./pages/Marketing";

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <Routes location={location}>
          {/* Landing (multi-page) — main route */}
          <Route path="/" element={<LandingHome />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:idOrSlug" element={<BlogPost />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/api-reference" element={<APIReference />} />
          <Route path="/status" element={<Status />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/security" element={<Security />} />

          {/* Public routes with optional language prefix: /:lang/... (e.g. /es/features). Default language has no prefix. */}
          <Route path="/:lang" element={<LanguageRouteWrapper />}>
            <Route index element={<LandingHome />} />
            <Route path="features" element={<Features />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="changelog" element={<Changelog />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:idOrSlug" element={<BlogPost />} />
            <Route path="careers" element={<Careers />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="help-center" element={<HelpCenter />} />
            <Route path="api-reference" element={<APIReference />} />
            <Route path="status" element={<Status />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="security" element={<Security />} />
            <Route path="login" element={<Login />} />
            <Route path="signup" element={<Signup />} />
          </Route>

          {/* App — dashboard & authenticated pages */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Index />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route path="/get-started" element={<ProtectedRoute><AppLayout><GetStarted /></AppLayout></ProtectedRoute>} />

          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* App pages */}
          <Route
            path="/stores"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Stores />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={<ProtectedRoute><AppLayout><Products /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/products/:productId"
            element={<ProtectedRoute><AppLayout><ProductDetails /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/orders"
            element={<ProtectedRoute><AppLayout><Orders /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/orders/:orderId"
            element={<ProtectedRoute><AppLayout><OrderDetails /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/customers"
            element={<ProtectedRoute><AppLayout><Customers /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/customers/:customerId/history"
            element={<ProtectedRoute><AppLayout><CustomerOrderHistory /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/users"
            element={<ProtectedRoute><AppLayout><Users /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/tasks"
            element={<ProtectedRoute><AppLayout><Tasks /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/tasks/:taskId"
            element={<ProtectedRoute><AppLayout><TaskDetails /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/analytics"
            element={<ProtectedRoute><AppLayout><Analytics /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/coupons"
            element={<ProtectedRoute><AppLayout><Coupons /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/billing"
            element={<ProtectedRoute><AppLayout><Billing /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/extensions"
            element={<ProtectedRoute><AppLayout><Extensions /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/conversations"
            element={<ProtectedRoute><AppLayout><Conversations /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/marketing"
            element={<ProtectedRoute><AppLayout><Marketing /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/support"
            element={<ProtectedRoute><AppLayout><Support /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/support/ticket/:ticketId"
            element={<ProtectedRoute><AppLayout><TicketDetail /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/support/article/:id"
            element={<ProtectedRoute><AppLayout><ArticleDetail /></AppLayout></ProtectedRoute>}
          />
          <Route
            path="/support/forum/:postId"
            element={<ProtectedRoute><AppLayout><Support /></AppLayout></ProtectedRoute>}
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const queryClient = new QueryClient();
  const dispatch = useDispatch();

  useEffect(() => {
    const lang = typeof localStorage !== "undefined" ? localStorage.getItem("preferredLanguage") : null;
    dispatch(loadAndSetLanguage(languageConfig.getLanguageCodes().includes(lang) ? lang : languageConfig.defaultLanguage));
  }, [dispatch]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <Toaster richColors />
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <AnimatedRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
